import { useSession } from '../../lib/auth/session.js';
import { useCdrStats, useExtensions, useTenants } from '../../lib/api/hooks.js';
import { Stat, StatGroup, StatusBadge, ErrorBanner, Empty } from '../../components/ui/Primitives.js';
import { formatDuration, formatMoney, formatNumber } from '../../lib/format.js';

export function DashboardPage() {
  const session = useSession((s) => s.session);
  const activeTenantId = useSession((s) => s.activeTenantId);
  const isPlatform = session?.scope.level === 'platform';

  const tenants = useTenants({ limit: 5 });
  const stats = useCdrStats(activeTenantId);
  const extensions = useExtensions(activeTenantId, { limit: 200 });

  const extCount = extensions.data?.data.length ?? 0;
  const activeExt = extensions.data?.data.filter((e) => e.status === 'active').length ?? 0;

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Dashboard</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            {isPlatform ? 'Platform overview across all tenants' : 'Your phone system at a glance'}
          </p>
        </div>
      </div>

      {stats.error && <ErrorBanner error={stats.error} />}

      <div style={{ marginBottom: 18 }}>
        <StatGroup>
          <Stat
            bare icon="phone" hue={1}
            label="Calls (30 days)"
            value={stats.isLoading ? '—' : formatNumber(stats.data?.total_calls ?? 0)}
            sub={stats.data ? `${stats.data.answered_calls} answered` : undefined}
          />
          <Stat
            bare icon="check" hue={2}
            label="Answer rate"
            value={stats.isLoading ? '—' : `${stats.data?.answer_rate_pct ?? 0}%`}
            tone={
              stats.data && stats.data.total_calls > 0
                ? stats.data.answer_rate_pct >= 70 ? 'ok'
                : stats.data.answer_rate_pct >= 40 ? 'warn' : 'danger'
                : undefined
            }
            sub="of all calls"
          />
          <Stat
            bare icon="clock" hue={4}
            label="Talk time"
            value={stats.isLoading ? '—' : formatDuration(stats.data?.total_duration_sec ?? 0)}
            sub={stats.data ? `avg ${formatDuration(stats.data.avg_duration_sec)}` : undefined}
          />
          <Stat
            bare icon="billing" hue={3}
            label="Spend (30 days)"
            value={stats.isLoading ? '—' : formatMoney(stats.data?.spend ?? null, stats.data?.currency ?? 'ZAR')}
            sub="excluding VAT"
          />
        </StatGroup>
      </div>

      <div className="arc-grid arc-grid-2">
        <div className="arc-card">
          <div className="arc-card-head">
            <h2 className="arc-h2">Extensions</h2>
            <span className="arc-subtle arc-num">{activeExt} of {extCount} active</span>
          </div>
          {extensions.isLoading ? (
            <div className="arc-card-body">
              <div className="arc-skeleton" style={{ height: 60 }} />
            </div>
          ) : extCount === 0 ? (
            <Empty icon="extension" title="No extensions yet" hint="Add an extension to start making calls." />
          ) : (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr><th>Number</th><th>Name</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {extensions.data?.data.slice(0, 6).map((e) => (
                    <tr key={e.id}>
                      <td className="arc-mono">{e.number}</td>
                      <td>{e.display_name}</td>
                      <td><StatusBadge status={e.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="arc-card">
          <div className="arc-card-head">
            <h2 className="arc-h2">{isPlatform ? 'Tenants' : 'Call mix (30 days)'}</h2>
          </div>
          {isPlatform ? (
            <div className="arc-table-wrap">
              <table className="arc-table">
                <thead>
                  <tr><th>Name</th><th>SIP domain</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {tenants.data?.data.map((t) => (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 550 }}>{t.name}</td>
                      <td className="arc-mono arc-subtle">{t.sip_domain}</td>
                      <td><StatusBadge status={t.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="arc-card-body">
              <dl className="arc-kv">
                <dt>Inbound</dt><dd className="arc-num">{stats.data?.inbound ?? 0}</dd>
                <dt>Outbound</dt><dd className="arc-num">{stats.data?.outbound ?? 0}</dd>
                <dt>Internal</dt><dd className="arc-num">{stats.data?.internal ?? 0}</dd>
              </dl>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
